import { ImportedListing } from '../../_types/imported-listing'
import { QuickPassUnitPreferences } from '../../_types/quickpass-profile'
import { MatchScoreBreakdown, PreferenceContribution } from './types'

const PRICE_WEIGHT = 35
const BEDROOM_WEIGHT = 18
const PROPERTY_TYPE_WEIGHT = 15
const CITY_WEIGHT = 18
const AVAILABILITY_WEIGHT = 8
const SCRAPE_QUALITY_WEIGHT = 6

/**
 * Computes how well an imported listing matches the renter's stated
 * preferences. Returns a 0–100 score plus a breakdown for tooltips or
 * future hover-explanations.
 *
 * Each axis contributes a weighted slice — price is the heaviest because
 * it's the most concrete, then beds / city / property type. Availability
 * and scrape-quality nudge the result so a great-on-paper match that is
 * unavailable scores below an equivalent available listing.
 *
 * @param {ImportedListing} listing - the listing to score
 * @param {QuickPassUnitPreferences | null} preferences - the renter prefs
 * @returns {MatchScoreBreakdown} the score + per-axis contributions
 */
const computeMatchScore = (
  listing: ImportedListing,
  preferences: QuickPassUnitPreferences | null,
): MatchScoreBreakdown => {
  if (preferences === null) {
    return { score: 0, contributions: [], hasPreferences: false }
  }

  const contributions: PreferenceContribution[] = []

  if (listing.priceMonthly !== null) {
    const min = preferences.priceMin
    const max = preferences.priceMax

    let earned = PRICE_WEIGHT
    let reason = 'Inside your budget.'

    if (min !== null && listing.priceMonthly < min) {
      const fraction = listing.priceMonthly / min
      earned = Math.round(PRICE_WEIGHT * fraction)
      reason = `Below your minimum (${min}).`
    } else if (max !== null && listing.priceMonthly > max) {
      const overshoot = listing.priceMonthly - max
      const tolerance = max * 0.25
      const penaltyFraction = Math.min(overshoot / tolerance, 1)
      earned = Math.round(PRICE_WEIGHT * (1 - penaltyFraction))
      reason = `${overshoot.toLocaleString('en-CA')} over your max.`
    }

    contributions.push({ weight: PRICE_WEIGHT, earned, reason })
  } else {
    contributions.push({
      weight: PRICE_WEIGHT,
      earned: Math.round(PRICE_WEIGHT * 0.5),
      reason: 'Price unknown — partial credit.',
    })
  }

  const beds = preferences.bedroomCounts
  if (listing.bedrooms !== null && beds.length > 0) {
    const matches = beds.includes(listing.bedrooms)
    const closest = beds.reduce(
      (closestSoFar, count) =>
        Math.abs(count - (listing.bedrooms as number)) <
        Math.abs(closestSoFar - (listing.bedrooms as number))
          ? count
          : closestSoFar,
      beds[0],
    )
    const distance = Math.abs(closest - listing.bedrooms)

    let earned = BEDROOM_WEIGHT
    let reason = 'Matches your bedroom count.'

    if (matches === false) {
      if (distance === 1) {
        earned = Math.round(BEDROOM_WEIGHT * 0.6)
        reason = 'One bedroom off your target.'
      } else {
        earned = Math.round(BEDROOM_WEIGHT * 0.25)
        reason = `${distance} bedrooms off your target.`
      }
    }

    contributions.push({ weight: BEDROOM_WEIGHT, earned, reason })
  } else {
    contributions.push({
      weight: BEDROOM_WEIGHT,
      earned: Math.round(BEDROOM_WEIGHT * 0.5),
      reason: 'Bedrooms unknown — partial credit.',
    })
  }

  const types = preferences.propertyTypes
  if (listing.propertyType !== null && types.length > 0) {
    const matches = types.includes(listing.propertyType)
    contributions.push({
      weight: PROPERTY_TYPE_WEIGHT,
      earned: matches === true ? PROPERTY_TYPE_WEIGHT : 0,
      reason:
        matches === true
          ? 'Matches your place type.'
          : 'Different place type than you wanted.',
    })
  } else if (types.length === 0) {
    contributions.push({
      weight: PROPERTY_TYPE_WEIGHT,
      earned: PROPERTY_TYPE_WEIGHT,
      reason: 'Open to any place type.',
    })
  }

  const cities = preferences.cities.map(city => city.toLowerCase())
  if (cities.length > 0 && listing.city !== '') {
    const matches = cities.includes(listing.city.toLowerCase())
    contributions.push({
      weight: CITY_WEIGHT,
      earned: matches === true ? CITY_WEIGHT : 0,
      reason:
        matches === true
          ? 'In one of your cities.'
          : `Not in your cities (in ${listing.city}).`,
    })
  } else if (cities.length === 0) {
    contributions.push({
      weight: CITY_WEIGHT,
      earned: CITY_WEIGHT,
      reason: 'Open to any city.',
    })
  }

  if (listing.isAvailable === true) {
    contributions.push({
      weight: AVAILABILITY_WEIGHT,
      earned: AVAILABILITY_WEIGHT,
      reason: 'Available now.',
    })
  } else if (listing.isAvailable === false) {
    contributions.push({
      weight: AVAILABILITY_WEIGHT,
      earned: 0,
      reason: 'Marked unavailable.',
    })
  } else {
    contributions.push({
      weight: AVAILABILITY_WEIGHT,
      earned: Math.round(AVAILABILITY_WEIGHT * 0.5),
      reason: 'Availability unknown.',
    })
  }

  if (listing.scrapeStatus === 'success') {
    contributions.push({
      weight: SCRAPE_QUALITY_WEIGHT,
      earned: SCRAPE_QUALITY_WEIGHT,
      reason: 'Listing fully parsed.',
    })
  } else if (listing.scrapeStatus === 'partial') {
    contributions.push({
      weight: SCRAPE_QUALITY_WEIGHT,
      earned: Math.round(SCRAPE_QUALITY_WEIGHT * 0.5),
      reason: 'Listing partially parsed.',
    })
  } else {
    contributions.push({
      weight: SCRAPE_QUALITY_WEIGHT,
      earned: 0,
      reason: 'Listing scrape failed.',
    })
  }

  const totalWeight = contributions.reduce(
    (sum, contribution) => sum + contribution.weight,
    0,
  )
  const totalEarned = contributions.reduce(
    (sum, contribution) => sum + contribution.earned,
    0,
  )

  const score =
    totalWeight === 0 ? 0 : Math.round((totalEarned / totalWeight) * 100)

  return {
    score: Math.max(0, Math.min(100, score)),
    contributions,
    hasPreferences: true,
  }
}

/**
 * Picks the badge tone variant based on a score band. 80+ is green,
 * 60-79 is primary purple, 40-59 is celebration yellow, < 40 is warmth.
 *
 * @param {number} score - the 0-100 match score
 * @returns {'great' | 'good' | 'okay' | 'low'} the tone bucket
 */
const resolveScoreTone = (score: number): 'great' | 'good' | 'okay' | 'low' => {
  if (score >= 80) {
    return 'great'
  }

  if (score >= 60) {
    return 'good'
  }

  if (score >= 40) {
    return 'okay'
  }

  return 'low'
}

/**
 * Picks a one-word label for a score band that summarises how good a
 * match is at a glance.
 *
 * @param {number} score - the 0-100 match score
 * @returns {string} a single-word band label
 */
const labelForScoreBand = (score: number): string => {
  if (score >= 80) {
    return 'Great match'
  }

  if (score >= 60) {
    return 'Good match'
  }

  if (score >= 40) {
    return 'Okay match'
  }

  return 'Loose match'
}

export { computeMatchScore, labelForScoreBand, resolveScoreTone }
